import { createSelector } from '@reduxjs/toolkit';
import {
    AbstractedUpsellItem,
    getMonthlyPriceWithOrWithoutDiscounts,
    isUpsellItem,
    PriceType,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import {
    selectAtomicId,
    selectError,
    selectExternalDiscounts,
    selectHardwareOnly,
    selectHasError,
    selectInsurance,
    selectRedPlusIds,
    selectSalesChannel,
    selectSelectedCategories,
    selectSelectedProducts,
    selectSimOnly,
    selectSubscriptionId,
} from './slice';
import { RootState } from '../../store';
import {
    getDeviceDetailAtomic,
    getDeviceDetailGroup,
    getProducts,
    getSimOnly,
    getTariffWithHardware,
} from './api/glados';
import { createStructuredSelector } from 'reselect';

const createAppSelector = createSelector.withTypes<RootState>();

const createProductsSelector = createAppSelector(
    [state => state],
    _state => getProducts.select(),
);
const createSimOnlySelector = createAppSelector(
    [state => state],
    _state => getSimOnly.select(),
);
const createTariffWithHardwareSelector = createAppSelector(
    [state => state],
    _state => getTariffWithHardware.select(),
);
const createDeviceDetailGroupSelector = createAppSelector(
    [state => state],
    _state => getDeviceDetailGroup.select(),
);
const createDeviceDetailAtomicSelector = createAppSelector(
    [state => state],
    _state => getDeviceDetailAtomic.select(),
);

const selectProductsQuery = createAppSelector(
    [
        state => state,
        createProductsSelector,
    ],
    (state, selector) => selector(state),
);

const selectSimOnlyQuery = createAppSelector(
    [
        state => state,
        createSimOnlySelector,
    ],
    (state, selector) => selector(state),
);

const selectTariffWithHardwareQuery = createAppSelector(
    [
        state => state,
        createTariffWithHardwareSelector,
    ],
    (state, selector) => selector(state),
);

const selectDeviceDetailGroupQuery = createAppSelector(
    [
        state => state,
        createDeviceDetailGroupSelector,
    ],
    (state, selector) => selector(state),
);

const selectDeviceDetailAtomicQuery = createAppSelector(
    [
        state => state,
        createDeviceDetailAtomicSelector,
    ],
    (state, selector) => selector(state),
);

const selectLoading = createAppSelector(
    [
        selectProductsQuery,
        selectSimOnlyQuery,
        selectTariffWithHardwareQuery,
        selectDeviceDetailGroupQuery,
        selectDeviceDetailAtomicQuery,
    ],
    (productsQuery, simOnlyQuery, tariffWithHardwareQuery, deviceDetailGroupQuery, deviceDetailAtomicQuery) =>
        productsQuery.isLoading
        || simOnlyQuery.isLoading
        || tariffWithHardwareQuery.isLoading
        || deviceDetailGroupQuery.isLoading
        || deviceDetailAtomicQuery.isLoading,
);

const selectCategories = createAppSelector(
    selectProductsQuery,
    query => query.data?.categories || [],
);

const selectProducts = createAppSelector(
    selectProductsQuery,
    query => query.data?.items || [],
);

const selectTariff = createAppSelector(
    [
        selectAtomicId,
        selectSubscriptionId,
        selectSimOnlyQuery,
        selectTariffWithHardwareQuery,
    ],
    (atomicId, subscriptionId, simOnlyQuery, tariffWithHardwareQuery) => {
        if (!subscriptionId) {
            return null;
        }

        if (atomicId) {
            return tariffWithHardwareQuery.data
                ?.find(offer => offer.hardware.atomicId === atomicId)?.tariffs
                .find(tariff => tariff.tariffId === subscriptionId) || null;
        }

        return simOnlyQuery.data?.tariffs
            .find(tariff => tariff.tariffId === subscriptionId) || null;
    },
);

const selectTariffPrice = createAppSelector(
    [
        selectTariff,
    ],
    tariff => {
        if (!tariff) {
            return null;
        }

        return getMonthlyPriceWithOrWithoutDiscounts(
            tariff.priceMonthlyTariffWithoutDiscounts,
            tariff.priceMonthlyTariffWithDiscounts,
        );
    },
);

const selectTariffPriceValue = createAppSelector(
    [
        selectExternalDiscounts,
        selectTariffPrice,
    ],
    (externalDiscounts, tariffPrice) =>
        tariffPrice?.gross
            ? Math.max(tariffPrice.gross - externalDiscounts[PriceType.Monthly], 0)
            : null,
);

const selectTariffName = createAppSelector(
    selectTariff,
    tariff => tariff?.tariffName || null,
);

const selectTariffRuntime = createAppSelector(
    selectTariffPrice,
    tariffPrice => tariffPrice?.recurrenceEnd,
);

const selectDevice = createAppSelector(
    [
        selectAtomicId,
        selectSubscriptionId,
        selectHardwareOnly,
        selectTariffWithHardwareQuery,
        selectDeviceDetailAtomicQuery,
    ],
    (atomicId, subscriptionId, isHardwareOnly, tariffWithHardwareQuery, deviceDetailAtomicQuery) => {
        if (!atomicId && !subscriptionId) {
            return null;
        }

        if (isHardwareOnly && deviceDetailAtomicQuery.isSuccess) {
            return deviceDetailAtomicQuery.data;
        }

        if (!tariffWithHardwareQuery.isSuccess) {
            return null;
        }

        const offer = tariffWithHardwareQuery.data.find(offerCandidate => offerCandidate.hardware.atomicId === atomicId);

        if (!offer) {
            return null;
        }

        return offer.hardware;
    },
);

const selectDevicePrice = createAppSelector(
    [
        selectExternalDiscounts,
        selectDevice,
        selectHardwareOnly,
    ],
    (externalDiscounts, device, isHardwareOnly) => {
        const price = isHardwareOnly ? device?.priceHardwareOnly?.gross : device?.priceHardwareOneTimeWithTariff?.gross;

        return price ? Math.max(price - externalDiscounts[PriceType.Onetime], 0) : null;
    },
);

const selectDeviceModelName = createAppSelector(
    selectDevice,
    device => device?.modelName || null,
);

const selectDeviceAtomicName = createAppSelector(
    selectDevice,
    device => device?.atomicName || null,
);

const selectMonthlyPrice = createAppSelector(
    [
        selectTariffPriceValue,
        selectSelectedProducts,
    ],
    // eslint-disable-next-line arrow-body-style
    (tariffPrice, selectedProducts): number | undefined => {
        // eslint-disable-next-line arrow-body-style
        return selectedProducts.reduce((previousProductPrice, product) => {
            if (!isUpsellItem(product)) {
                return 0;
            }

            // eslint-disable-next-line arrow-body-style
            return previousProductPrice + (product.price?.reduce((previousValuePrice, price) => {
                return previousValuePrice + (price.type === PriceType.Monthly ? price.value : 0);
            }, 0));
        }, tariffPrice || 0) || tariffPrice || undefined;
    },
);

const selectOneTimePrice = createAppSelector(
    [
        selectDevicePrice,
        selectSelectedProducts,
    ],
    // eslint-disable-next-line arrow-body-style
    (devicePrice, selectedProducts): number | undefined => {
        // eslint-disable-next-line arrow-body-style
        return selectedProducts.reduce((previousProductPrice, product) => {
            if (!isUpsellItem(product)) {
                return 0;
            }

            // eslint-disable-next-line arrow-body-style
            return previousProductPrice + (product.price?.reduce((previousValuePrice, price) => {
                return previousValuePrice + (price.type === PriceType.Onetime ? price.value : 0);
            }, 0));
        }, devicePrice || 0) || devicePrice || undefined;
    },
);

const selectProduct = (productId: AbstractedUpsellItem['id']) =>
    createAppSelector(
        selectProducts,
        products => products?.find(product => product.id === productId),
    );

const selectTrackingPayload = createAppSelector(
    [
        selectSalesChannel,
        selectAtomicId,
        selectDeviceAtomicName,
        selectDevicePrice,
        selectTariffName,
        selectSubscriptionId,
        selectTariffRuntime,
        selectTariffPriceValue,
        selectSelectedProducts,
        selectSelectedCategories,
        selectDevice,
    ],
    (
        salesChannel,
        atomicId,
        atomicName,
        devicePrice,
        tariffName,
        subscriptionId,
        tariffRuntime,
        tariffMonthlyPrice,
        selectedProducts,
        selectedCategories,
        selectedDevice,
    ) => ({
        salesChannel,
        atomicId,
        atomicName,
        devicePrice,
        tariffName,
        subscriptionId,
        tariffRuntime,
        tariffMonthlyPrice,
        selectedProducts,
        selectedCategories,
        selectedDevice,
    }),
);

const selectProductsByCategory = createAppSelector(
    [
        selectProducts,
        selectSelectedCategories,
        selectSimOnly,
        selectHardwareOnly,
    ],
    (products, selectedCategories, isSimOnly, isHardwareOnly) => {
        if (selectedCategories.length === 0 && !isSimOnly && !isHardwareOnly) {
            return products;
        }

        return products.filter(product =>
            (isSimOnly && product.category.value === UpsellFacetValue.Redplus)
                || (isHardwareOnly && product.category.value === UpsellFacetValue.Accessory)
                || (!isHardwareOnly && !isSimOnly && selectedCategories
                    .some(category => category.value === product.category.value)));
    },
);

export {
    selectLoading,
    selectCategories,
    selectProducts,
    selectTariffName,
    selectTariffRuntime,
    selectTariffPriceValue,
    selectDevice,
    selectDevicePrice,
    selectDeviceModelName,
    selectDeviceAtomicName,
    selectMonthlyPrice,
    selectOneTimePrice,
    selectProduct,
    selectTrackingPayload,
    selectProductsByCategory,
};

export const createStructuredAppSelector =
    createStructuredSelector.withTypes<RootState>();

export const mapStateToProps = createStructuredAppSelector({
    isLoading: selectLoading,
    categories: selectCategories,
    products: selectProductsByCategory,
    insurance: selectInsurance,
    redPlusIds: selectRedPlusIds,
    tariffName: selectTariffName,
    deviceName: selectDeviceModelName,
    monthlyPrice: selectMonthlyPrice,
    oneTimePrice: selectOneTimePrice,
    selectedCategories: selectSelectedCategories,
    selectedProducts: selectSelectedProducts,
    isHardwareOnly: selectHardwareOnly,
    isSimOnly: selectSimOnly,
    error: selectError,
    hasError: selectHasError,
});
